import { Box, CircularProgress, Container } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useLogout } from "api/services/useAuth";
import { useAppSelector } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";

interface InvitationAcceptancePageProps {
  invitationToken?: string;
}

const REDIRECT_TIME = 10;

export function InvitationAcceptancePage({ invitationToken: propToken }: InvitationAcceptancePageProps) {
  const { token: paramToken } = useParams<{ token: string }>();
  const token = propToken || paramToken;
  const navigate = useNavigate();
  const userData = useAppSelector(getUserState);
  const [redirectTime, setRedirectTime] = useState(REDIRECT_TIME);

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { refetch: logout } = useLogout();

  useEffect(() => {
    const processInvitation = async () => {
      if (!token) {
        setError("Invalid invitation link");
        return;
      }
      // If user is logged in, show message and handle logout
      if (userData.id) {
        // as the token is a base64 encoded string, we need to decode it
        let decodedUrl = "";
        try {
          decodedUrl = atob(token);
        } catch (err) {
          console.error("Error decoding invitation URL", err);
          setError("Invalid invitation link");
          setLoaded(false);
          return;
        }

        // Logout from the current account
        try {
          const res = await logout();

          if (res?.logged_out) {
            // After logout or if not logged in, redirect to the invitation URL
            setTimeout(() => {
              window.location.href = decodedUrl;
            }, REDIRECT_TIME * 1000);

            intervalRef.current = setInterval(() => {
              setRedirectTime((prev) => Math.max(0, prev - 1));
            }, 1000);

            setLoaded(true);
          } else {
            setError("Failed to logout from current account");
            setLoaded(false);
          }
        } catch (err) {
          console.error("Error logging out from current account", err);
          setError("Failed to logout from current account");
          setLoaded(false);
        }
      }
    };
    processInvitation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

  }, [token, userData, navigate, logout]);



  return (
    <Box id="invitation-acceptance-page" className="h-[calc(100%-50px)]">
      <Container
        maxWidth="sm"
        className="h-[calc(100%-70px)]"
        sx={{
          marginBottom: "20px",
          px: { xs: "10px", sm: "20px", md: "30px" },
        }}
      >
        <Box className="flex flex-col items-center justify-center p-8 h-full">
          {error ? (

            <Typography variant="subtitle" color="error">
              {error}
            </Typography>
          ) : (
            <>
              <CircularProgress />
              {loaded && userData.email && (
                <Typography align="center" className="mt-4">
                  You will be logged out from account <b>{userData.email}</b> and
                  redirected to set the new password for the new account in <b>{redirectTime}</b> seconds
                </Typography>
              )}
            </>
          )
          }
        </Box>
      </Container>
    </Box>
  )
} 
